import 'letteringjs';
import PubSub from 'pubsub-js';

// Constants
import { EVENTS } from '@/scripts/constants';

// Others
import {
  AnimatedListLine, customSelect,
  Dropdown,
  HeaderNav,
  initGoogleMaps,
  PageFilters,
  RollHoverBtn,
  swiperSliderAdvanced, TabFilters, Tabs,
  DynamicHoverLink,
  UltraHover,
  // CtaVideo,
} from '@/components';

import { AnimatedSection, selectors } from '@/scripts/animation';

import {
  counterScene,
  headerScene, parallaxImage,
  scrollToTopScene,
  simpleRevealer,
  stripesRevealer,
} from '@/components/_scrollmagic';
// import { leadZero, resizeCover, addTask, runAllTasks, Nearby, LimitRows } from '@/scripts/utils';
import { leadZero, addTask, resizeCover, runAllTasks, Nearby, initNavigation } from '@/scripts/utils';

import plugins from './plugins';

import initCoverSlider from '../initCoverSlider';

const initBasics = (container, usePJAX) => new Promise((resolve) => {
  const basics = [];

  addTask(() => basics.push(Nearby));

  // responsive table
  addTask(() => { $(container).find('.editable table').responsiveTable(); });

  // limit text rows
  // addTask(() => {
  //   container.querySelectorAll('[data-limit-rows]')
  //     .forEach((el) => {
  //       basics.push(new LimitRows({ el }));
  //     });
  // });
  // Dynamic Hover
  addTask(() => {
    container.querySelectorAll('[data-role*="dynamic-hover-link"]')
      .forEach((el) => {
        basics.push(new DynamicHoverLink({ el }));
      });
  });
  // Ultra Hover
  addTask(() => {
    container.querySelectorAll('[data-role="ultra-hover"]')
      .forEach((element) => {
        basics.push(new UltraHover({ element }));
      });
  });
  // init animated list line
  addTask(() => {
    container.querySelectorAll('[data-role*="animated-list-line"]')
      .forEach((list) => {
        basics.push(new AnimatedListLine(list));
      });
  });

  // init tab-filters
  addTask(() => {
    container.querySelectorAll('[data-role*="grid-with-filters"]')
      .forEach((el) => {
        basics.push(new TabFilters({
          el,
          transitionDuration: 700,
          lineController: el.querySelector('[data-role*="animated-list-line"]'),
        }));
      });
  });

  // tabs init
  addTask(() => {
    container.querySelectorAll('[data-role*="tab-list-container"]')
      .forEach((el) => {
        basics.push(new Tabs({
          mainContainer: el,
          lineController: el.querySelector('[data-role*="animated-list-line"]'),
        }));
      });
  });

  // init dropdowns
  addTask(() => {
    container.querySelectorAll('[data-role*="dropdown-container"]')
      .forEach((element) => {
        basics.push(new Dropdown({
          element,
        }));
      });
  });

  // init page-filters form
  addTask(() => {
    basics.push(new PageFilters({
      allowPopstate: !usePJAX,
      element: container.querySelector('[data-role*="page-filters-container"]'),
    }));
  });

  // Lead zero 1 -> 01, 11 -> 11
  addTask(() => { $(container).find('[data-lead-zero="true"] span').text((i, text) => leadZero(text.trim())); });

  // Add current year
  addTask(() => { $(container).find('.current-year').text((new Date()).getFullYear()); });

  // split-text
  addTask(() => {
    $(container).find('[data-split-text="true"]').text((i, text) => text.trim()).lettering('words')
      .children('span')
      .lettering();
  });

  addTask(() => {
    $(container).find('[data-split-text="button-letters"]').find('span').text((i, text) => text.trim())
      .lettering();
  });

  addTask(() => {
    $(container).find('[data-split-text="letters-only"]').text((i, text) => text.trim()).lettering();
  });

  // roll-hover buttons
  addTask(() => {
    container.querySelectorAll('[data-role*="roll-hover-btn"]')
      .forEach((button) => {
        basics.push(new RollHoverBtn(button));
      });
  });

  // hasn-navigation
  addTask(() => {
    initNavigation();
  });

  // google maps
  addTask(() => {
    container.querySelectorAll('[data-role*="google-maps-container"]')
      .forEach((map) => {
        basics.push(initGoogleMaps(map));
      });
  });

  // selects
  addTask(() => {
    container.querySelectorAll('[data-role*="custom-select"]')
      .forEach((el) => {
        basics.push(customSelect(el));
      });
  });

  // Main Menu
  addTask(() => {
    basics.push(new HeaderNav({ // eslint-disable-line no-new
      container,
      menuScrollElement: '[data-role="menu"]',
      transitionDuration: 450, // $_menu-duration in scss file.
    }));
  });

  // resize cover section
  addTask(() => {
    basics.push(resizeCover(container));
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(basics); }), { timeout: 1000 });
});

const initSliders = container => new Promise((resolve) => {
  const sliders = [];

  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'two-halves',
      slidesPerView: 1,
      spaceBetween: 0,
      pagination: true,
      // restOptions: {
      //   effect: 'fade',
      //   fadeEffect: {
      //     crossFade: true,
      //   },
      // },
    }));
  });
  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'another-text',
      slidesPerView: 1,
      spaceBetween: 32,
      autoHeight: true,
      watchSlidesProgress: true,
      watchOverflow: false,
      speed: 300,
      pagination: {
        el: '.swiper-pagination',
        type: 'fraction ',
      },
      breakpoints: {
        1024: {
          slidesPerView: 1,
          spaceBetween: 15,
        },
        640: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    }));
  });

  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'another-card-slide',
      slidesPerView: 3,
      spaceBetween: 32,
      watchOverflow: true,
      watchSlidesProgress: false,
      autoplay: true,
      navigation: true,
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
        640: {
          slidesPerView: 1,
          spaceBetween: 32,
        },
        320: {
          slidesPerView: 1,
          spaceBetween: 32,
        },
      },
    }));
  });
  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'another-items',
      slidesPerView: 3,
      speed: 400,
      breakpoints: {
        1024: {
          slidesPerView: 2.2,
          spaceBetween: 15,
        },
        640: {
          slidesPerView: 1.2,
          spaceBetween: 15,
        },
      },
    }));
  });
  addTask(() => {
    const [cardSwiper] = swiperSliderAdvanced({
      container,
      selector: 'another-card',
      slidesPerView: 1,
      speed: 600,
      // virtualTranslate: true,
    });
    if (!(cardSwiper instanceof Object)) {
      return;
    }
    // const { slides } = cardSwiper;
    //
    // cardSwiper.on('slideChange', () => {
    //   const { activeIndex } = cardSwiper;
    //   let index = 1;
    //   for (let i = activeIndex + 1; i < slides.length; i += 1) {
    //     const card = slides[i].querySelector('.card');
    //     if (i === activeIndex + 1) {
    //       card.style.transition = ' opacity 0.5s linear 0.7s';
    //     } else {
    //       card.style.transform = `translateX(${-64 * index}%); transition: transform 0.7s linear`;
    //       index += 1;
    //     }
    //   }
    // });
    cardSwiper.slideTo(1, 0);
    // cardSwiper.slideTo(0, 0);

    sliders.push(cardSwiper);
  });
  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'two-halves-texts',
      cropContainer: true,
      slidesPerView: 1,
      connect: 'two-halves-images',
    }));
  });

  addTask(() => {
    sliders.push(swiperSliderAdvanced({
      container,
      selector: 'swiper-container',
      cropContainer: true,
      slidesPerView: 1,
      pagination: true,
      autoplay: false,
    }));
  });

  addTask(initCoverSlider);


  requestIdleCallback(deadline => runAllTasks(deadline, () => {
    resolve(sliders);
  }), { timeout: 1000 });
});

const initScrollMagic = container => new Promise((resolve) => {
  const scenes = [];
  // build scrollMagic
  addTask(() => {
    Array
      .from(container.querySelectorAll(selectors.section))
      .map(section => new AnimatedSection(section))
      .forEach((scene) => {
        scenes.push(scene);
        scene.init();
      });
  });

  addTask(() => scenes.push(scrollToTopScene(container)));
  addTask(() => scenes.push(...stripesRevealer(container)));
  addTask(() => scenes.push(...simpleRevealer(container)));
  addTask(() => scenes.push(...parallaxImage(container)));
  addTask(() => scenes.push(headerScene(container)));
  addTask(() => scenes.push(...counterScene(container)));
  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(scenes); }), { timeout: 1000 });
});

export default function initPlugins(container, usePJAX = false) {
  return new Promise(async (resolve) => {
    const inited = [
      ...await initBasics(container, usePJAX),
      ...await initSliders(container, usePJAX),
      ...await initScrollMagic(container, usePJAX),
    ];

    if (inited) {
      plugins.push(...inited);
      PubSub.publish(EVENTS.lazyImages_update);
      container.querySelectorAll('.cover [data-lazy-loading]').forEach((lazyImage) => {
        PubSub.publish(EVENTS.lazyImages_loadForce, { lazyImage });
      });
      requestIdleCallback(() => resolve(true), { timeout: 1000 });
    }
  });
}



// WEBPACK FOOTER //
// ./scripts/plugins/initPlugins.js